import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} CarouselRefs
 * @property {HTMLElement} track - The scrolling container.
 * @property {HTMLElement[]} [items] - The slides.
 * @property {HTMLButtonElement} [previous] - Previous control.
 * @property {HTMLButtonElement} [next] - Next control.
 */

/**
 * A carousel built on native scrolling rather than transforms.
 *
 * The track is an ordinary overflow container with scroll snapping, so it
 * already works with a trackpad, a touch swipe, a scrollbar drag and the
 * keyboard before any of this runs. The component only adds the two controls
 * and keeps their disabled state honest — which means that if the script fails
 * to load, the section degrades to something still fully usable.
 *
 * Paging moves by whole items rather than by a fixed distance, so a section
 * showing three across on a desktop and one on a phone needs no configuration
 * to match.
 *
 * @extends {Component<CarouselRefs>}
 */
class CarouselComponent extends Component {
  requiredRefs = ['track'];

  /** @type {number} */
  #frame = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.refs.track.addEventListener('scroll', this.#requestUpdate, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#requestUpdate);
    this.#resizeObserver.observe(this.refs.track);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track.removeEventListener('scroll', this.#requestUpdate);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** Scrolls back one page. */
  previous() {
    this.#page(-1);
  }

  /** Scrolls forward one page. */
  next() {
    this.#page(1);
  }

  /**
   * Moves by one page in the given direction.
   * @param {1 | -1} direction
   */
  #page(direction) {
    const { track } = this.refs;
    const distance = this.#pageDistance();

    track.scrollBy({
      left: distance * direction,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * The distance one page covers: as many whole items as currently fit, so the
   * carousel advances by a screenful rather than by a slide.
   *
   * @returns {number}
   */
  #pageDistance() {
    const { track } = this.refs;
    const items = this.#items;
    const first = items[0];

    if (!first) return track.clientWidth;

    const step = this.#itemStep(items);
    const perPage = Math.max(1, Math.round(track.clientWidth / step));

    return step * perPage;
  }

  /**
   * Distance from one item's leading edge to the next, gap included. Measured
   * rather than read from CSS so a section can change its gap freely.
   *
   * @param {HTMLElement[]} items
   * @returns {number}
   */
  #itemStep(items) {
    const [first, second] = items;

    if (!first) return this.refs.track.clientWidth;
    if (second) return Math.abs(second.offsetLeft - first.offsetLeft) || first.offsetWidth;

    return first.offsetWidth;
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    const { items } = this.refs;

    if (Array.isArray(items)) return items;
    return items ? [items] : [];
  }

  #requestUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Reflects how far the track has scrolled onto the controls.
   *
   * The one-pixel tolerance absorbs the fractional scroll positions that
   * fractional layout widths and browser zoom produce, which would otherwise
   * leave the last control enabled with nowhere left to go.
   */
  #update = () => {
    const { track, previous, next } = this.refs;
    const overflow = track.scrollWidth - track.clientWidth;
    const scrollable = overflow > 1;
    // Right-to-left locales scroll to negative offsets, so compare on distance.
    const travelled = Math.abs(track.scrollLeft);

    if (previous) previous.disabled = !scrollable || travelled <= 1;
    if (next) next.disabled = !scrollable || travelled >= overflow - 1;

    this.toggleAttribute('data-scrollable', scrollable);
  };
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
